import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL } from '@/constants/api';
import { authService } from './auth';

export interface AnalyticsConnection {
  id: string;
  name: string;
  email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  property_id?: string;
  view_id?: string;
  website_url?: string;
  website_name?: string;
}

export interface GoogleAnalyticsProperty {
  id: string;
  name: string;
  website_url: string;
  view_id: string;
  view_name: string;
}

export interface AnalyticsDashboardData {
  sessions: number;
  users: number;
  pageviews: number;
  bounce_rate: number;
  avg_session_duration: number;
  top_pages: Array<{
    page: string;
    views: number;
    bounce_rate: number;
  }>;
  traffic_sources: Array<{
    source: string;
    sessions: number;
    percentage: number;
  }>;
  date_range: {
    start_date: string;
    end_date: string;
  };
}

class AnalyticsService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests if available
    this.api.interceptors.request.use((config) => {
      const token = authService.getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Build Google OAuth URL with proper parameters
   */
  private buildGoogleOAuthUrl(): string {
    const clientId = process.env.REACT_APP_GOOGLE_ANALYTICS_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.error('Available env vars:', Object.keys(process.env).filter(key => key.includes('GOOGLE')));
      throw new Error('Google Analytics configuration not found');
    }

    const redirectUri = `${window.location.origin}/dashboard/oauth-callback`;
    const scope = 'https://www.googleapis.com/auth/analytics.readonly https://www.googleapis.com/auth/analytics.manage.users.readonly https://www.googleapis.com/auth/webmasters.readonly';
    
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: scope,
      access_type: 'offline',
      prompt: 'consent'
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  /**
   * Start the OAuth flow for Google Analytics
   * This redirects the user to Google's OAuth consent page in the same window
   */
  startOAuthFlow(): void {
    // Build Google OAuth URL directly
    const oauthUrl = this.buildGoogleOAuthUrl();
    
    // Redirect to Google OAuth in the same window
    window.location.href = oauthUrl;
  }

  /**
   * Get all analytics connections for the current user
   */
  async getConnections(): Promise<AnalyticsConnection[]> {
    const response = await this.api.get('/analytics/connections/');
    return response.data?.connections || [];
  }

  /**
   * Get details for a specific analytics connection
   */
  async getConnection(connectionId: string): Promise<AnalyticsConnection> {
    const response = await this.api.get(`/analytics/connections/${connectionId}/`);
    return response.data;
  }

  /**
   * Delete an analytics connection
   */
  async deleteConnection(connectionId: string): Promise<void> {
    await this.api.delete(`/analytics/connections/${connectionId}/delete/`);
  }

  /**
   * Fetch analytics dashboard data for a specific connection
   */
  async getDashboardData(
    connectionId: string,
    startDate?: string,
    endDate?: string
  ): Promise<AnalyticsDashboardData> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await this.api.post(`/analytics/dashboard/${connectionId}/`, params);
    return response.data;
  }

  /**
   * Check if user has any active analytics connections
   * Only one connection allowed per organization
   */
  async hasActiveConnections(): Promise<boolean> {
    try {
      const connections = await this.getConnections();
      return connections.some(conn => conn.is_active);
    } catch (error) {
      console.error('Error checking analytics connections:', error);
      return false;
    }
  }

  /**
   * Get the current active analytics connection
   * Only one connection allowed per organization 
   */
  async getActiveConnection(): Promise<AnalyticsConnection | null> {
    try {
      const connections = await this.getConnections();
      return connections.find(conn => conn.is_active) || null;
    } catch (error) {
      console.error('Error getting active connection:', error);
      return null;
    }
  }

  /**
   * Get available Google Analytics properties for the user
   * This will be called after OAuth to let user choose which website to connect
   */
  async getAvailableProperties(): Promise<GoogleAnalyticsProperty[]> {
    const response = await this.api.get('/analytics/properties/', {
      headers: {
        'Authorization': `Bearer ${authService.getAuthToken()}`
      }
    });
    
    // Transform the API response to match the expected interface
    const apiData = response.data;
    if (!apiData.properties || !Array.isArray(apiData.properties)) {
      return [];
    }
    
    const transformedProperties: GoogleAnalyticsProperty[] = [];
    
    apiData.properties.forEach((item: any) => {
      // Handle the new API response structure
      if (item.property_id && item.property_name) {
        const property = {
          id: item.property_id,
          name: item.property_name,
          website_url: item.website_url || '',
          view_id: item.view_id || '',
          view_name: item.view_name || ''
        };
        
        // Debug logging
        console.log('Transformed property:', property);
        
        transformedProperties.push(property);
      }
    });
    
    return transformedProperties;
  }

  /**
   * Save the selected property as the organization's analytics connection
   * This replaces any existing connection (one connection per org rule)
   */
  async saveSelectedProperty(propertyId: string, viewId: string): Promise<AnalyticsConnection> {
    console.log('Analytics Service: Saving property', { propertyId, viewId });
    console.log('Analytics Service: Auth token available:', !!authService.getAuthToken());
    console.log('Analytics Service: API base URL:', this.api.defaults.baseURL);
    
    const response = await this.api.post(`/analytics/connections/${propertyId}/mark-active/`, {
      view_id: viewId
    });
    
    console.log('Analytics Service: Response received:', response.status, response.data);
    return response.data;
  }

  // Widget Data Fetching Methods

  /**
   * Fetch data for total sessions widget
   */
  async fetchTotalSessions(): Promise<number> {
    try {
      const response = await this.api.get('/analytics/sessions/');
      return response.data?.total_sessions || 0;
    } catch (error) {
      console.error('Error fetching total sessions:', error);
      return 0;
    }
  }

  /**
   * Fetch data for total users widget
   */
  async fetchTotalUsers(): Promise<number> {
    try {
      const response = await this.api.get('/analytics/users/');
      return response.data?.total_users || 0;
    } catch (error) {
      console.error('Error fetching total users:', error);
      return 0;
    }
  }

  /**
   * Fetch data for total page views widget
   */
  async fetchTotalPageViews(): Promise<number> {
    try {
      const response = await this.api.get('/analytics/pageviews/');
      return response.data?.total_pageviews || 0;
    } catch (error) {
      console.error('Error fetching total page views:', error);
      return 0;
    }
  }

  /**
   * Fetch data for bounce rate widget
   */
  async fetchBounceRate(): Promise<number> {
    try {
      const response = await this.api.get('/analytics/bounce-rate/');
      return response.data?.bounce_rate || 0;
    } catch (error) {
      console.error('Error fetching bounce rate:', error);
      return 0;
    }
  }

  /**
   * Fetch data for sessions over time widget
   */
  async fetchSessionsOverTime(): Promise<Array<{ name: string; value: number }>> {
    try {
      const response = await this.api.get('/analytics/sessions-over-time/');
      return response.data?.data || this.generateTimeSeriesData('sessions');
    } catch (error) {
      console.error('Error fetching sessions over time:', error);
      return this.generateTimeSeriesData('sessions');
    }
  }

  /**
   * Fetch data for users over time widget
   */
  async fetchUsersOverTime(): Promise<Array<{ name: string; value: number }>> {
    try {
      const response = await this.api.get('/analytics/users-over-time/');
      return response.data?.data || this.generateTimeSeriesData('users');
    } catch (error) {
      console.error('Error fetching users over time:', error);
      return this.generateTimeSeriesData('users');
    }
  }

  /**
   * Fetch data for page views over time widget
   */
  async fetchPageViewsOverTime(): Promise<Array<{ name: string; value: number }>> {
    try {
      const response = await this.api.get('/analytics/pageviews-over-time/');
      return response.data?.data || this.generateTimeSeriesData('pageviews');
    } catch (error) {
      console.error('Error fetching page views over time:', error);
      return this.generateTimeSeriesData('pageviews');
    }
  }

  /**
   * Fetch data for bounce rate trend widget
   */
  async fetchBounceRateTrend(): Promise<Array<{ name: string; value: number }>> {
    try {
      const response = await this.api.get('/analytics/bounce-rate-trend/');
      return response.data?.data || this.generateTimeSeriesData('bounce_rate');
    } catch (error) {
      console.error('Error fetching bounce rate trend:', error);
      return this.generateTimeSeriesData('bounce_rate');
    }
  }

  /**
   * Fetch data for top pages widget
   */
  async fetchTopPages(): Promise<Array<{ page: string; sessions: number; bounceRate: string }>> {
    try {
      const response = await this.api.get('/analytics/top-pages/');
      return response.data?.data || this.generateTopPagesData();
    } catch (error) {
      console.error('Error fetching top pages:', error);
      return this.generateTopPagesData();
    }
  }

  /**
   * Fetch data for traffic sources widget
   */
  async fetchTrafficSources(): Promise<Array<{ name: string; value: number; color: string }>> {
    try {
      const response = await this.api.get('/analytics/traffic-sources/');
      return response.data?.data || this.generateTrafficSourcesData();
    } catch (error) {
      console.error('Error fetching traffic sources:', error);
      return this.generateTrafficSourcesData();
    }
  }

  /**
   * Fetch data for organic traffic widget
   */
  async fetchOrganicTraffic(): Promise<Array<{ name: string; value: number }>> {
    try {
      const response = await this.api.get('/analytics/organic-traffic/');
      return response.data?.data || this.generateTimeSeriesData('organic');
    } catch (error) {
      console.error('Error fetching organic traffic:', error);
      return this.generateTimeSeriesData('organic');
    }
  }

  /**
   * Fetch data for organic CTR widget
   */
  async fetchOrganicCTR(): Promise<number> {
    try {
      const response = await this.api.get('/analytics/organic-ctr/');
      return response.data?.ctr || 0;
    } catch (error) {
      console.error('Error fetching organic CTR:', error);
      return 0;
    }
  }

  /**
   * Fetch data for organic revenue widget
   */
  async fetchOrganicRevenue(): Promise<number> {
    try {
      const response = await this.api.get('/analytics/organic-revenue/');
      return response.data?.revenue || 0;
    } catch (error) {
      console.error('Error fetching organic revenue:', error);
      return 0;
    }
  }

  /**
   * Fetch data for organic impressions widget
   */
  async fetchOrganicImpressions(): Promise<Array<{ name: string; value: number }>> {
    try {
      const response = await this.api.get('/analytics/organic-impressions/');
      return response.data?.data || this.generateTimeSeriesData('impressions');
    } catch (error) {
      console.error('Error fetching organic impressions:', error);
      return this.generateTimeSeriesData('impressions');
    }
  }

  /**
   * Fetch data for average ranking position widget
   */
  async fetchAvgRankingPosition(): Promise<Array<{ name: string; value: number }>> {
    try {
      const response = await this.api.get('/analytics/avg-ranking-position/');
      return response.data?.data || this.generateTimeSeriesData('ranking');
    } catch (error) {
      console.error('Error fetching average ranking position:', error);
      return this.generateTimeSeriesData('ranking');
    }
  }

  /**
   * Fetch data for branded vs non-branded traffic widget
   */
  async fetchBrandedVsNonBrandedTraffic(): Promise<Array<{ name: string; value: number; color: string }>> {
    try {
      const response = await this.api.get('/analytics/branded-vs-nonbranded/');
      return response.data?.data || this.generateBrandedTrafficData();
    } catch (error) {
      console.error('Error fetching branded vs non-branded traffic:', error);
      return this.generateBrandedTrafficData();
    }
  }

  /**
   * Fetch data for conversions from SEO widget
   */
  async fetchConversionsFromSEO(): Promise<Array<{ name: string; value: number }>> {
    try {
      const response = await this.api.get('/analytics/conversions-from-seo/');
      return response.data?.data || this.generateTimeSeriesData('conversions');
    } catch (error) {
      console.error('Error fetching conversions from SEO:', error);
      return this.generateTimeSeriesData('conversions');
    }
  }

  /**
   * Fetch data for device performance widget
   */
  async fetchDevicePerformance(): Promise<Array<{ name: string; value: number; color: string }>> {
    try {
      const response = await this.api.get('/analytics/device-performance/');
      return response.data?.data || this.generateDevicePerformanceData();
    } catch (error) {
      console.error('Error fetching device performance:', error);
      return this.generateDevicePerformanceData();
    }
  }

  /**
   * Fetch data for average engagement time widget
   */
  async fetchAvgEngagementTime(): Promise<Array<{ name: string; value: number }>> {
    try {
      const response = await this.api.get('/analytics/avg-engagement-time/');
      return response.data?.data || this.generateTimeSeriesData('engagement');
    } catch (error) {
      console.error('Error fetching average engagement time:', error);
      return this.generateTimeSeriesData('engagement');
    }
  }

  /**
   * Fetch data for top geo locations widget
   */
  async fetchTopGeoLocations(): Promise<Array<{ name: string; value: number; color: string }>> {
    try {
      const response = await this.api.get('/analytics/top-geo-locations/');
      return response.data?.data || this.generateGeoLocationsData();
    } catch (error) {
      console.error('Error fetching top geo locations:', error);
      return this.generateGeoLocationsData();
    }
  }

  /**
   * Generic method to fetch data for any widget
   */
  async fetchWidgetData(widgetId: string): Promise<any> {
    const fetchMethods: { [key: string]: () => Promise<any> } = {
      'total-sessions': () => this.fetchTotalSessions(),
      'total-users': () => this.fetchTotalUsers(),
      'total-pageviews': () => this.fetchTotalPageViews(),
      'bounce-rate': () => this.fetchBounceRate(),
      'sessions-over-time': () => this.fetchSessionsOverTime(),
      'users-over-time': () => this.fetchUsersOverTime(),
      'pageviews-over-time': () => this.fetchPageViewsOverTime(),
      'bounce-rate-trend': () => this.fetchBounceRateTrend(),
      'top-pages': () => this.fetchTopPages(),
      'traffic-sources': () => this.fetchTrafficSources(),
      'organic-traffic': () => this.fetchOrganicTraffic(),
      'organic-ctr': () => this.fetchOrganicCTR(),
      'organic-revenue': () => this.fetchOrganicRevenue(),
      'organic-impressions': () => this.fetchOrganicImpressions(),
      'avg-ranking-position': () => this.fetchAvgRankingPosition(),
      'branded-vs-nonbranded-traffic': () => this.fetchBrandedVsNonBrandedTraffic(),
      'conversions-from-seo': () => this.fetchConversionsFromSEO(),
      'device-performance': () => this.fetchDevicePerformance(),
      'avg-engagement-time': () => this.fetchAvgEngagementTime(),
      'top-geo-locations': () => this.fetchTopGeoLocations(),
    };

    const fetchMethod = fetchMethods[widgetId];
    if (fetchMethod) {
      return await fetchMethod();
    }

    console.warn(`No fetch method found for widget: ${widgetId}`);
    return null;
  }

  // Helper methods for generating fallback data
  private generateTimeSeriesData(type: string): Array<{ name: string; value: number }> {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const baseValue = this.getBaseValueForType(type);
    
    return months.map(month => ({
      name: month,
      value: Math.floor(baseValue * (Math.random() * 0.3 + 0.7))
    }));
  }

  private getBaseValueForType(type: string): number {
    switch (type) {
      case 'sessions':
        return 10000;
      case 'users':
        return 8000;
      case 'pageviews':
        return 15000;
      case 'bounce_rate':
        return 45;
      case 'organic':
        return 4000;
      case 'impressions':
        return 20000;
      case 'ranking':
        return 15; // Average ranking position
      case 'conversions':
        return 200;
      case 'engagement':
        return 180; // Average engagement time in seconds
      default:
        return 1000;
    }
  }

  private generateTopPagesData(): Array<{ page: string; sessions: number; bounceRate: string }> {
    const totalSessions = 10000;
    return [
      { page: 'Homepage', sessions: Math.floor(totalSessions * 0.3), bounceRate: '45%' },
      { page: '/products', sessions: Math.floor(totalSessions * 0.2), bounceRate: '52%' },
      { page: '/about', sessions: Math.floor(totalSessions * 0.15), bounceRate: '38%' },
      { page: '/contact', sessions: Math.floor(totalSessions * 0.1), bounceRate: '60%' },
      { page: '/blog', sessions: Math.floor(totalSessions * 0.08), bounceRate: '42%' }
    ];
  }

  private generateTrafficSourcesData(): Array<{ name: string; value: number; color: string }> {
    const totalSessions = 10000;
    return [
      { name: 'Organic', value: Math.floor(totalSessions * 0.4), color: '#8884d8' },
      { name: 'Direct', value: Math.floor(totalSessions * 0.3), color: '#82ca9d' },
      { name: 'Social', value: Math.floor(totalSessions * 0.15), color: '#ffc658' },
      { name: 'Referral', value: Math.floor(totalSessions * 0.1), color: '#ff7300' },
      { name: 'Email', value: Math.floor(totalSessions * 0.05), color: '#00ff00' }
    ];
  }

  private generateBrandedTrafficData(): Array<{ name: string; value: number; color: string }> {
    const totalSessions = 10000;
    return [
      { name: 'Branded', value: Math.floor(totalSessions * 0.3), color: '#8884d8' },
      { name: 'Non-Branded', value: Math.floor(totalSessions * 0.7), color: '#82ca9d' }
    ];
  }

  private generateDevicePerformanceData(): Array<{ name: string; value: number; color: string }> {
    const totalSessions = 10000;
    return [
      { name: 'Mobile', value: Math.floor(totalSessions * 0.6), color: '#8884d8' },
      { name: 'Desktop', value: Math.floor(totalSessions * 0.35), color: '#82ca9d' },
      { name: 'Tablet', value: Math.floor(totalSessions * 0.05), color: '#ffc658' }
    ];
  }

  private generateGeoLocationsData(): Array<{ name: string; value: number; color: string }> {
    const totalSessions = 10000;
    return [
      { name: 'United States', value: Math.floor(totalSessions * 0.4), color: '#8884d8' },
      { name: 'United Kingdom', value: Math.floor(totalSessions * 0.2), color: '#82ca9d' },
      { name: 'Canada', value: Math.floor(totalSessions * 0.15), color: '#ffc658' },
      { name: 'Australia', value: Math.floor(totalSessions * 0.1), color: '#ff7300' },
      { name: 'Germany', value: Math.floor(totalSessions * 0.08), color: '#00ff00' },
      { name: 'Others', value: Math.floor(totalSessions * 0.07), color: '#ff00ff' }
    ];
  }

  // Dashboard Data Fetching Methods

  /**
   * Fetch daily visits data for dashboard charts and metrics
   */
  async fetchDailyVisits(period: string = '365', groupBy: string = 'day'): Promise<any> {
    try {
      const response = await this.api.get('/analytics/daily-visits/', {
        params: { 
          period,
          group_by: groupBy
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching daily visits:', error);
      return null;
    }
  }

  /**
   * Fetch referral websites data
   */
  async fetchReferralWebsites(period: string = '365'): Promise<any> {
    try {
      const response = await this.api.get('/analytics/referral-websites/', {
        params: { period }
      });
      return response.data?.data || null;
    } catch (error) {
      console.error('Error fetching referral websites:', error);
      return null;
    }
  }

  /**
   * Fetch referral keywords data
   */
  async fetchReferralKeywords(period: string = '365'): Promise<any> {
    try {
      const response = await this.api.get('/analytics/referral-keywords/', {
        params: { period }
      });
      return response.data?.data || null;
    } catch (error) {
      console.error('Error fetching referral keywords:', error);
      return null;
    }
  }

  /**
   * Fetch top pages data
   */
  async fetchTopPagesData(period: string = '365'): Promise<any> {
    try {
      const response = await this.api.get('/analytics/top-pages/', {
        params: { period }
      });
      return response.data?.data || null;
    } catch (error) {
      console.error('Error fetching top pages:', error);
      return null;
    }
  }

  /**
   * Fetch devices and browsers data
   */
  async fetchDevicesBrowsers(period: string = '365'): Promise<any> {
    try {
      const response = await this.api.get('/analytics/devices-browsers/', {
        params: { period }
      });
      return response.data?.data || null;
    } catch (error) {
      console.error('Error fetching devices and browsers:', error);
      return null;
    }
  }

  /**
   * Fetch countries data
   */
  async fetchCountries(period: string = '365'): Promise<any> {
    try {
      const response = await this.api.get('/analytics/countries/', {
        params: { period }
      });
      return response.data?.data || null;
    } catch (error) {
      console.error('Error fetching countries:', error);
      return null;
    }
  }

  /**
   * Fetch all dashboard data in one call
   */
  async fetchAllDashboardData(period: string = '365', groupBy: string = 'day'): Promise<{
    dailyVisits: any;
    referralWebsites: any;
    referralKeywords: any;
    topPages: any;
    devicesBrowsers: any;
    countries: any;
  }> {
    try {
      const [
        dailyVisits,
        referralWebsites,
        referralKeywords,
        topPages,
        devicesBrowsers,
        countries
      ] = await Promise.all([
        this.fetchDailyVisits(period, groupBy),
        this.fetchReferralWebsites(period),
        this.fetchReferralKeywords(period),
        this.fetchTopPagesData(period),
        this.fetchDevicesBrowsers(period),
        this.fetchCountries(period)
      ]);

      return {
        dailyVisits,
        referralWebsites,
        referralKeywords,
        topPages,
        devicesBrowsers,
        countries
      };
    } catch (error) {
      console.error('Error fetching all dashboard data:', error);
      return {
        dailyVisits: null,
        referralWebsites: null,
        referralKeywords: null,
        topPages: null,
        devicesBrowsers: null,
        countries: null
      };
    }
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;
